import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  VideoCall,
  Group,
  Event,
  School,
  Favorite,
  Grass,
  Diamond,
  Search,
  BookOnline,
  Support,
  Email,
  Phone,
  Chat,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Styled components
const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 700,
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 14px rgba(99,102,241,0.4)",
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  border: "1px solid #f0f0f0",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
  },
}));

const SuitCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  height: "100%",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 16px 48px rgba(0,0,0,0.15)",
  },
}));

const HelpPage = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState<number | false>(false);

  const handleFAQChange =
    (index: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFAQ(isExpanded ? index : false);
    };

  const suits = [
    {
      name: "Heart Suit",
      icon: <Favorite sx={{ fontSize: 40, color: "#e91e63" }} />,
      color: "#e91e63",
      description: "Live Entertainment & Interactive Shows",
      features: [
        "Live streaming sessions",
        "Interactive performances",
        "Real-time audience engagement",
        "Entertainment-focused content",
      ],
    },
    {
      name: "Spade Suit",
      icon: <School sx={{ fontSize: 40, color: "#2196f3" }} />,
      color: "#2196f3",
      description: "Learning & Educational Content",
      features: [
        "Educational workshops",
        "Skill development sessions",
        "Tutorial and training content",
        "Knowledge sharing platforms",
      ],
    },
    {
      name: "Clover Suit",
      icon: <Grass sx={{ fontSize: 40, color: "#4caf50" }} />,
      color: "#4caf50",
      description: "Events & Community Gatherings",
      features: [
        "Community events",
        "Social gatherings",
        "Event hosting and participation",
      ],
    },
    {
      name: "Diamond Suit",
      icon: <Diamond sx={{ fontSize: 40, color: "#ff9800" }} />,
      color: "#ff9800",
      description: "Commerce & Business Services",
      features: [
        "Product demonstrations",
        "Sales and marketing sessions",
        "Professional services",
      ],
    },
  ];

  const sessionTypes = [
    {
      title: "One-on-One Sessions",
      icon: <VideoCall sx={{ fontSize: 40, color: "#6366F1" }} />,
      description: "Personalized private sessions with talents",
      features: [
        "Direct video calls with talents",
        "Personalized attention",
        "Custom session duration",
        "Private consultation space",
      ],
    },
    {
      title: "Group Sessions",
      icon: <Group sx={{ fontSize: 40, color: "#9F03FF" }} />,
      description: "Interactive group experiences with multiple participants",
      features: [
        "Multi-participant sessions",
        "Group learning experiences",
        "Shared interaction space",
        "Community building",
      ],
    },
    {
      title: "StageMode Sessions",
      icon: <Event sx={{ fontSize: 40, color: "#FF6B35" }} />,
      description: "Large audience live events and performances",
      features: [
        "Large-scale live events",
        "Audience participation",
        "Live streaming capabilities",
        "Event management tools",
      ],
    },
    {
      title: "Coffee Calls",
      icon: <Chat sx={{ fontSize: 40, color: "#34D399" }} />,
      description: "Quick consultation and networking sessions",
      features: [
        "Short duration calls",
        "Quick consultations",
        "Networking opportunities",
        "Flexible scheduling",
      ],
    },
  ];

  const faqs = [
    {
      question: "What is StageLink?",
      answer:
        "StageLink is a live interaction platform that connects users with talents through real-time sessions and events across four suits: Heart (Entertainment), Spade (Learning), Clover (Events), and Diamond (Commerce). It's designed to provide personalized, interactive experiences in the experience economy.",
    },
    {
      question: "How do I book a session?",
      answer:
        "To book a session: 1) Browse available talents and their cards, 2) Select your preferred time slot, 3) Choose session type (one-on-one, group, or StageMode), 4) Complete payment, 5) Receive confirmation and join details. You can also request custom sessions through our booking system.",
    },
    {
      question: "What are the different session types?",
      answer:
        "StageLink offers four main session types: One-on-One Sessions for personalized private interactions, Group Sessions for multi-participant experiences, StageMode Sessions for large audience events, and Coffee Calls for quick consultations and networking.",
    },
    {
      question: "How does the suit system work?",
      answer:
        "The suit system categorizes content and experiences: Heart Suit for entertainment and live shows, Spade Suit for learning and education, Clover Suit for events and community gatherings, and Diamond Suit for commerce and business services. Each suit offers tailored experiences for different interests and needs.",
    },
    {
      question: "Is it free to join StageLink?",
      answer:
        "Yes, creating an account on StageLink is completely free. However, individual sessions may have different pricing depending on the talent, session type, and duration. Some sessions may be free while others are ticketed events.",
    },
    {
      question: "How do I become a talent on the platform?",
      answer:
        "To become a talent: 1) Register for an account, 2) Complete your talent profile, 3) Choose your suits and specialties, 4) Set your availability and pricing, 5) Create your first card/session. Our team will review and approve your profile.",
    },
    {
      question: "What payment methods are accepted?",
      answer:
        "StageLink accepts various payment methods including credit/debit cards, digital wallets, and other secure payment options. All transactions are processed securely through our payment partners.",
    },
    {
      question: "How do I join a session?",
      answer:
        "Once you've booked a session, you'll receive a confirmation with session details. At the scheduled time, click the 'Join Session' button in your dashboard or use the provided link. Make sure your camera and microphone are working for the best experience.",
    },
    {
      question: "Can I cancel or reschedule a booking?",
      answer:
        "Yes, you can cancel or reschedule bookings according to the talent's cancellation policy. Most talents allow cancellations up to 24 hours before the session. Check the specific talent's terms for their cancellation policy.",
    },
    {
      question: "How do I contact support?",
      answer:
        "You can contact our support team through multiple channels: email support, live chat, or phone support. We're available 24/7 to help with any questions or issues you may have.",
    },
  ];

  const quickActions = [
    {
      title: "Browse Talents",
      icon: <Search sx={{ fontSize: 30, color: "#6366F1" }} />,
      action: () => navigate("/search?tab=talents"),
    },
    {
      title: "Book a Session",
      icon: <BookOnline sx={{ fontSize: 30, color: "#9F03FF" }} />,
      action: () => navigate("/cards"),
    },
    {
      title: "View Schedule",
      icon: <Event sx={{ fontSize: 30, color: "#FF6B35" }} />,
      action: () => navigate("/schedule"),
    },
    {
      title: "Contact Support",
      icon: <Support sx={{ fontSize: 30, color: "#34D399" }} />,
      action: () => navigate("/contact"),
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: "#333",
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            How Can We Help You?
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#666", mb: 4, maxWidth: "600px", mx: "auto" }}
          >
            Everything you need to know about StageLink - from booking sessions to
            understanding our unique suit system
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Quick Actions
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {quickActions.map((action, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    cursor: "pointer",
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                    },
                  }}
                  onClick={action.action}
                >
                  <Box sx={{ mb: 2 }}>{action.icon}</Box>
                  <Typography variant="body2" fontWeight={600}>
                    {action.title}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Suits Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Understanding Our Suits
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            StageLink organizes content into four distinct suits, each offering
            unique experiences tailored to different interests and needs.
          </Typography>
          <Grid container spacing={4}>
            {suits.map((suit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <SuitCard>
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: `${suit.color}15`,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{suit.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1, color: suit.color }}
                    >
                      {suit.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                      {suit.description}
                    </Typography>
                    <Box>
                      {suit.features.map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          sx={{
                            m: 0.5,
                            backgroundColor: `${suit.color}20`,
                            color: suit.color,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </SuitCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Session Types */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Session Types
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Choose from different session formats to match your preferences and
            needs.
          </Typography>
          <Grid container spacing={4}>
            {sessionTypes.map((session, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Box sx={{ mb: 2 }}>{session.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {session.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                      {session.description}
                    </Typography>
                    <Box>
                      {session.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          sx={{
                            color: "#555",
                            mb: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          • {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How It Works */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            How It Works
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                step: "1",
                title: "Create Account",
                description: "Sign up for free and complete your profile",
                icon: "👤",
              },
              {
                step: "2",
                title: "Browse Talents",
                description: "Explore talents across all four suits",
                icon: "🔍",
              },
              {
                step: "3",
                title: "Book Session",
                description: "Choose your preferred time and session type",
                icon: "📅",
              },
              {
                step: "4",
                title: "Join & Enjoy",
                description: "Connect live and enjoy your experience",
                icon: "🎉",
              },
            ].map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: "16px",
                    height: "100%",
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: "#6366F1",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "1.2rem",
                    }}
                  >
                    {step.step}
                  </Box>
                  <Box sx={{ mt: 2, mb: 2, fontSize: "3rem" }}>{step.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {step.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: "800px", mx: "auto" }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFAQ === index}
                onChange={handleFAQChange(index)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#6366F1" }} />}
                  sx={{
                    px: 3,
                    py: 2,
                    borderLeft: "4px solid #6366F1",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 2 }}>
                  <Typography sx={{ color: "#555", fontSize: "0.95rem" }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Contact Support */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "20px",
            p: 4,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Still Need Help?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", mb: 4, maxWidth: "600px", mx: "auto" }}
          >
            Our support team is here to help you with any questions or issues.
            Reach out to us through any of these channels.
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              {
                icon: <Email />,
                title: "Email Support",
                contact: "support@taasnet.com",
              },
              {
                icon: <Phone />,
                title: "Phone Support",
                contact: "+1 (555) 123-4567",
              },
              { icon: <Chat />, title: "Live Chat", contact: "Available 24/7" },
            ].map((contact, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Box sx={{ textAlign: "center" }}>
                  <Box sx={{ mb: 2, color: "#6366F1" }}>{contact.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {contact.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {contact.contact}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 4 }}>
            <GradientButton
                onClick={() => navigate("/contact")}
                startIcon={<Email />}
            >
              Contact Support
            </GradientButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HelpPage;
